import { Form, Modal, Select, message } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { patchFieldTechForSupplier } from '../../thunks/field_techs_thunks';
import { problemTypesHeirarchicalForSupplier } from '../../thunks/problem_types_thunks';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import SelectWithAllToggle from '../common/SelectWithAllToggle';

const EditProblemTypesModal: FC<any> = ({
	onCancel,
	onSuccess,
	email,
	problemTypeIds: initialProblemTypeIds,
	problemTypes,
	fetchProblemTypes,
	patchFieldTech,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [updating, setUpdating] = useState(false);

	// Convention shared with FieldTechForm: an empty problemTypeIds list means "all problem types".
	const initialHasAccessToAll = (initialProblemTypeIds || []).length === 0;

	const handleSubmit = useCallback(
		(values) => {
			const hasAccessToAllProblemTypes = !!values.hasAccessToAllProblemTypes;
			setUpdating(true);
			patchFieldTech({
				email,
				problemTypeIds: hasAccessToAllProblemTypes
					? []
					: nullSafeGetOrElse('problemTypeIds', values, []),
			})
				.then(() => onSuccess && onSuccess())
				.catch(() => message.error('Unable to update problem types.'))
				.finally(() => setUpdating(false));
		},
		[email, onSuccess, patchFieldTech]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title={'Edit Problem Types'}
			okText={'Save'}
			onCancel={onCancel}
			onOk={form.submit}
			closable={true}
			confirmLoading={updating}
			destroyOnClose={true}
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				preserve={false}
				initialValues={{
					hasAccessToAllProblemTypes: initialHasAccessToAll,
					problemTypeIds: initialProblemTypeIds || [],
				}}
				onFinish={handleSubmit}
			>
				<SelectWithAllToggle
					label={'Problem Types'}
					name={'problemTypeIds'}
					allName={'hasAccessToAllProblemTypes'}
					form={form}
					selectAllPlaceholder={'All problem types'}
				>
					<ProblemTypeTreeSelect
						allowClear={true}
						mode="multiple"
						stateSlice={problemTypes}
						targetCollectionName="analyticsProblemTypesDropDown"
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							fetchProblemTypes(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						fetchMultiple={() => {}}
						renderRecord={(problemType) => (
							<Select.Option key={problemType.id} value={problemType.id}>
								{problemType.name}
							</Select.Option>
						)}
					/>
				</SelectWithAllToggle>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
});

const mapDispatchToProps = (dispatch) => ({
	patchFieldTech: (payload) => dispatch(patchFieldTechForSupplier(payload)),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			problemTypesHeirarchicalForSupplier(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditProblemTypesModal);
